/* Doc-type typeahead — platform + org catalog with "Add '…' as a new type".

   value = catalog key (string) | ''. onChange(key, entry) fires with the
   picked entry so callers can also set docView. Coining a type writes
   orgs/{org}/docTypes/{key} (source 'org') and selects it immediately. */
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useToast } from './Toast';
import { DOC_FAMILIES, DOC_FAMILY_LABELS } from '../data/deals';
import { useDocTypeCatalog, searchDocTypes, exactDocType, addOrgDocType, familyDefaultView } from '../data/docTypes';
import './DocTypePicker.css';

export default function DocTypePicker({ orgId, value, onChange, placeholder = 'Document type (e.g. Side Letter, MSA, Memo)…', suggestion = null, compact = false, inputId }) {
  const { user } = useAuth();
  const toast = useToast();
  const uid = useId();
  const { catalog, byKey } = useDocTypeCatalog(orgId);
  const current = value ? byKey.get(value) : null;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState(null);     // { label, family } while the "new type" mini-form is shown
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  const results = useMemo(() => searchDocTypes(catalog, query, { limit: 8 }), [catalog, query]);
  const typed = query.trim();
  const exact = typed ? exactDocType(catalog, typed) : null;
  const canAdd = !!orgId && typed.length >= 2 && !exact;

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) { setOpen(false); setAdding(null); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (entry) => {
    onChange?.(entry.key, entry);
    setQuery('');
    setOpen(false);
    setAdding(null);
  };

  const startAdd = () => setAdding({ label: typed, family: guessFamily(typed) });

  const confirmAdd = async () => {
    if (!adding || busy) return;
    setBusy(true);
    try {
      const entry = await addOrgDocType(orgId, user, { label: adding.label, family: adding.family, view: familyDefaultView(adding.family) });
      toast(`"${entry.label}" added to your organization's document types`, 'ok');
      pick(entry);
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    const n = results.length + (canAdd ? 1 : 0);
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, n - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (active < results.length) pick(results[active]);
      else if (canAdd) startAdd();
    } else if (e.key === 'Escape') { setOpen(false); setAdding(null); }
  };

  const listId = `${uid}-list`;
  return (
    <div className={`dtp${compact ? ' dtp-compact' : ''}`} ref={rootRef} data-nova-note="Document type picker: type to search the catalog; Enter picks; unknown names offer 'Add as a new type'">
      <div className="dtp-control">
        {current && !open ? (
          <button type="button" className="dtp-current" onClick={() => setOpen(true)} aria-haspopup="listbox" aria-expanded={open}>
            <span className="dtp-current-label">{current.label}</span>
            <span className="dtp-meta">{DOC_FAMILY_LABELS[current.family] || current.family}{current.scope === 'org' ? ' · yours' : ''}</span>
            <span className="dtp-caret" aria-hidden>▾</span>
          </button>
        ) : (
          <input
            id={inputId}
            className="cs-input dtp-input"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            value={query}
            placeholder={current ? current.label : placeholder}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setAdding(null); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            autoComplete="off"
          />
        )}
        {current && !open && (
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm dtp-clear" onClick={() => { onChange?.('', null); setOpen(true); }} aria-label="Change document type">Change</button>
        )}
      </div>

      {suggestion && !current && !open && (
        <div className="dtp-suggest u-small">
          {suggestion.entry ? (
            <>
              Smart import says <strong>{suggestion.entry.label}</strong>
              {suggestion.confidence < 0.95 ? ` (${Math.round(suggestion.confidence * 100)}% match)` : ''}.{' '}
              <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => pick(suggestion.entry)}>Use it</button>
            </>
          ) : suggestion.proposed ? (
            <>
              Smart import detected <strong>{suggestion.proposed}</strong>, not in the catalog yet.{' '}
              {orgId && (
                <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setQuery(suggestion.proposed); setOpen(true); setAdding({ label: suggestion.proposed, family: guessFamily(suggestion.proposed) }); }}>
                  Add as a new type
                </button>
              )}
            </>
          ) : null}
        </div>
      )}

      {open && (
        <div className="dtp-pop" role="presentation">
          {!adding ? (
            <ul className="dtp-list" role="listbox" id={listId} aria-label="Document types">
              {results.map((e, i) => (
                <li
                  key={`${e.scope}:${e.key}`}
                  role="option"
                  aria-selected={i === active}
                  className={`dtp-opt${i === active ? ' is-active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(ev) => { ev.preventDefault(); pick(e); }}
                >
                  <span className="dtp-opt-label">{e.label}</span>
                  <span className="dtp-meta">{DOC_FAMILY_LABELS[e.family] || e.family}{e.scope === 'org' ? ' · yours' : ''}</span>
                </li>
              ))}
              {canAdd && (
                <li
                  role="option"
                  aria-selected={active === results.length}
                  className={`dtp-opt dtp-add${active === results.length ? ' is-active' : ''}`}
                  onMouseEnter={() => setActive(results.length)}
                  onMouseDown={(ev) => { ev.preventDefault(); startAdd(); }}
                >
                  <span className="dtp-opt-label">＋ Add “{typed}” as a new type</span>
                  <span className="dtp-meta">for your organization</span>
                </li>
              )}
              {!results.length && !canAdd && (
                <li className="dtp-empty u-small u-muted">{typed ? 'No matching type' : 'Loading catalog…'}</li>
              )}
            </ul>
          ) : (
            <div className="dtp-addform u-stack" style={{ '--gap': 'var(--sp-2)' }}>
              <div className="u-small">New document type for your organization</div>
              <input className="cs-input" value={adding.label} onChange={(e) => setAdding({ ...adding, label: e.target.value })} aria-label="Type name" autoFocus />
              <select className="cs-input" value={adding.family} onChange={(e) => setAdding({ ...adding, family: e.target.value })} aria-label="Family">
                {DOC_FAMILIES.map((f) => <option key={f} value={f}>{DOC_FAMILY_LABELS[f]}</option>)}
              </select>
              <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || adding.label.trim().length < 2} onClick={confirmAdd}>{busy ? 'Adding…' : 'Add type'}</button>
                <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setAdding(null)}>Back</button>
              </div>
              <div className="cs-help">Agreements get deal-point tracking; other families render as documents. Types your team uses often are proposed to the platform catalog.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function guessFamily(label) {
  const s = String(label || '').toLowerCase();
  if (/\b(agreement|contract|lease|licen[cs]e|nda|amendment|addendum|terms|sow|statement of work|letter of intent|loi)\b/.test(s)) return 'agreement';
  if (/\b(note|safe|loan|credit|warrant|term sheet|cap table|model|subscription)\b/.test(s)) return 'financing';
  if (/\b(resolution|minutes|bylaws|articles|certificate|consent|charter)\b/.test(s)) return 'corporate';
  if (/\b(filing|form|10-k|10-q|8-k|annual report)\b/.test(s)) return 'filing';
  if (/\b(employment|offer letter|severance|handbook|commission|option|equity plan|contractor)\b/.test(s)) return 'employment';
  if (/\b(patent|trademark|copyright|ip|invention)\b/.test(s)) return 'ip';
  if (/\b(complaint|motion|brief|discovery|subpoena|affidavit|order|judgment|settlement)\b/.test(s)) return 'litigation';
  if (/\b(tax|k-1|w-9|1120|1065)\b/.test(s)) return 'tax';
  if (/\b(rfp|rfi|rfq|solicitation|policy|compliance|permit|regulat)\b/.test(s)) return 'regulatory';
  if (/\b(memo|report|analysis|summary|deck|presentation|proposal|checklist|schedule|invoice|letter)\b/.test(s)) return 'deliverable';
  return 'other';
}
